import { takeLatest, put, call } from 'redux-saga/effects';
import get from 'lodash/get';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
/*
 * Apply/remove reuse the existing draft-order adjustment endpoints.
 * For "get applied rewards" we currently reuse getAdjustment (GET
 * draft-order/adjustment/discounts), which returns the applied REWARDSCERT
 * adjustments. Once the dedicated "applied rewards" endpoint is confirmed and
 * published, swap getAdjustment -> getAppliedRewards (already added in
 * yoda-interfaces src). See open questions.
 */
import {
    applyAdjustment,
    removeAdjustment,
    getAdjustment,
} from 'yoda-interfaces/lib/Order/OrderApi';
import {
    GET_APPLIED_CASHPASS,
    GET_APPLIED_CASHPASS_SUCCESS,
    GET_APPLIED_CASHPASS_ERROR,
    APPLY_CASHPASS,
    APPLY_CASHPASS_SUCCESS,
    APPLY_CASHPASS_ERROR,
    REMOVE_CASHPASS,
    REMOVE_CASHPASS_SUCCESS,
    REMOVE_CASHPASS_ERROR,
} from '../actionTypes/CashpassCartActionTypes';
import { setCashpassLoading } from '../actions/CashpassCartAction';

const REWARDSCERT = 'REWARDSCERT';
const ADJUSTMENT_NOT_FOUND = 'API_ERR_ADJUSTMENT_NOT_FOUND';
// Sent as the `source` request header on apply so the backend can identify
// that the apply-adjustment request originated from the Rewards Center.
const REWARDS_CENTER_SOURCE = 'REWARDS_CENTER';

/**
 * Extracts the first error entry from a service response. The API returns
 * `data` as either an array of errors or a single error object.
 * @param {Object} response - the axios-like response ({ status, data }).
 * @returns {Object} error info with an optional errorMessage/errorCode.
 */
export const getErrorInfo = (response) => {
    const data = get(response, 'data');
    if (Array.isArray(data)) {
        return data[0] || {};
    }
    return data || {};
};

/**
 * Normalizes the applied-rewards response into the array of applied REWARDSCERT
 * adjustments. Supports either a bare array (new GET API sample) or a
 * draft-order style payload with an `adjustments` array.
 * @param {Object} response
 * @returns {Array<Object>}
 */
export const extractAppliedRewards = (response) => {
    const data = get(response, 'data');
    const list = Array.isArray(data) ? data : get(data, 'adjustments', []);
    return (Array.isArray(list) ? list : []).filter((item) => item && item.code === REWARDSCERT);
};

export function* getAppliedCashpassSaga(action) {
    yield put(setCashpassLoading(true));
    try {
        const response = get(
            yield call(FactorySaga, getAdjustment, {
                ...action,
                // FactorySaga forwards only `payload` (and a few whitelisted keys)
                // to the API fn, so `source` must live inside payload. getAdjustment
                // pulls it into the `source` request header. GET has no params, so
                // it is used purely as a header (like applyAdjustment on the POST).
                payload: { ...(action.payload || {}), source: REWARDS_CENTER_SOURCE },
            }),
            'response',
            {}
        );
        const appliedRewards = extractAppliedRewards(response);
        yield put({ type: GET_APPLIED_CASHPASS_SUCCESS, appliedRewards });
    } catch (error) {
        yield put({ type: GET_APPLIED_CASHPASS_ERROR, error });
    } finally {
        // Clear the loader once results (success or error) are out.
        yield put(setCashpassLoading(false));
    }
}

export function* applyCashpassSaga(action) {
    const certificate = action.payload || {};
    const { serialNumber } = certificate;
    // POST expects the code without the "-" separator and the serialNumber.
    const code = String(get(certificate, 'code', '')).replace('-', '');
    yield put(setCashpassLoading(true));
    try {
        const response = get(
            yield call(FactorySaga, applyAdjustment, {
                ...action,
                // FactorySaga forwards only `payload` (and a few whitelisted keys)
                // to the API fn, so `source` must live inside payload. applyAdjustment
                // pulls it into the `source` request header and excludes it from the
                // POST params (without mutating payload, so it survives a 401 retry).
                payload: { code, serialNumber, source: REWARDS_CENTER_SOURCE },
            }),
            'response',
            {}
        );
        if (response.status === 201) {
            yield put({ type: APPLY_CASHPASS_SUCCESS, serialNumber });
            // Refresh applied rewards to capture the new adjustment id (source of truth).
            yield put({ type: GET_APPLIED_CASHPASS });
        } else {
            const errorInfo = getErrorInfo(response);
            // errorMessage captured for future UI (design in progress).
            yield put({
                type: APPLY_CASHPASS_ERROR,
                serialNumber,
                errorMessage: errorInfo.errorMessage || null,
                errorCode: errorInfo.errorCode || null,
            });
        }
    } catch (error) {
        yield put({
            type: APPLY_CASHPASS_ERROR,
            serialNumber,
            errorMessage: get(error, 'errorMessage', null),
            error,
        });
    } finally {
        // Clear the loader once results (success or error) are out.
        yield put(setCashpassLoading(false));
    }
}

export function* removeCashpassSaga(action) {
    const { id, serialNumber } = action.payload || {};
    yield put(setCashpassLoading(true));
    try {
        const response = get(
            yield call(FactorySaga, removeAdjustment, { ...action, payload: id }),
            'response',
            {}
        );
        const errorInfo = getErrorInfo(response);
        if (response.status === 204 || errorInfo.errorCode === ADJUSTMENT_NOT_FOUND) {
            // 204 = removed; 404/NOT_FOUND = already gone -> treat as removed.
            yield put({ type: REMOVE_CASHPASS_SUCCESS, id, serialNumber });
            yield put({ type: GET_APPLIED_CASHPASS });
        } else {
            // errorMessage captured for future UI (design in progress).
            yield put({
                type: REMOVE_CASHPASS_ERROR,
                serialNumber,
                errorMessage: errorInfo.errorMessage || null,
                errorCode: errorInfo.errorCode || null,
            });
        }
    } catch (error) {
        yield put({
            type: REMOVE_CASHPASS_ERROR,
            serialNumber,
            errorMessage: get(error, 'errorMessage', null),
            error,
        });
    } finally {
        // Clear the loader once results (success or error) are out.
        yield put(setCashpassLoading(false));
    }
}

export default function* watchCashpassCartSaga() {
    yield takeLatest(GET_APPLIED_CASHPASS, getAppliedCashpassSaga);
    yield takeLatest(APPLY_CASHPASS, applyCashpassSaga);
    yield takeLatest(REMOVE_CASHPASS, removeCashpassSaga);
}

watchCashpassCartSaga.sagaName = 'cashpassCartSaga';
